import { Transform, Type, Expose } from 'class-transformer';
import { DocumentSnapshot } from 'firebase/firestore';
import { _FirebaseDocument, forceMap, anyToDate } from './Models';

export enum DatasourceStatus {
    PENDING = "pending",
    CONNECTED = "connected",
    ERROR = "error",
    INVALID_CREDENTIALS = "invalid_credentials",
    REVOKED = "revoked",
    TO_DELETE = "to_delete",
}

export enum DatasourceType {
    GOOGLE_DRIVE = "google_drive",
    OFFICE_365 = "office_365",
    MANUAL_UPLOAD = "manual_upload",
    TRACEX = "tracex",
    QUALISHARE = "qualishare",
    ODOO = "odoo",
    ATLASSIAN = "atlassian",
}

export enum DatasourceTrigger {
    IDLE = "idle",
    REQUESTED = "requested",
    SCHEDULED = "scheduled",
}

export class DatasourceGoogleDriveMetadataDrive {
    @Expose()
    public drive_id: string;
    @Expose()
    public drive_name: string;
    @Expose()
    public drive_type: string; // "my_drive" | "shared_drive"
    @Expose()
    public root_folder_id: string;
    @Expose()
    public syncing: boolean = false;
    @Expose()
    @Transform(anyToDate)
    public last_sync_time?: Date;
}

export class DatasourceGoogleDriveMetadata {
    @Expose()
    public google_sub: string;
    @Expose()
    public email?: string;
    @Expose()
    public scopes: string[] = [];
    @Expose()
    public start_page_token?: string;
    @Expose()
    public secret_name?: string;
    @Expose()
    @Transform(anyToDate)
    public refresh_token_expire_at?: Date;
    @Expose()
    @Transform(forceMap(DatasourceGoogleDriveMetadataDrive), { toClassOnly: true })
    public drives: Map<string, DatasourceGoogleDriveMetadataDrive> = new Map();
}

export class DatasourceGoogleOAuth {
    @Expose()
    public code: string;
    @Expose()
    public code_verifier: string;
    @Expose()
    public redirect_uri: string;
    @Expose()
    public scopes: string[] = [];
    @Expose()
    @Transform(anyToDate)
    public issued_at?: Date;
}

export class DatasourceOffice365MetadataDrive {
    @Expose()
    public syncing: boolean = false;
    @Expose()
    @Transform(anyToDate)
    public last_sync_time?: Date;

    @Expose()
    public drive_id: string;
    @Expose()
    public drive_name?: string;
    @Expose()
    public drive_root_item_id?: string;
    @Expose()
    public drive_root_item_name?: string;
    @Expose()
    public drive_next_link?: string;
    @Expose()
    public drive_delta_link?: string;
}


export class DatasourceOffice365MetadataSite {
    @Expose()
    public site_id: string;
    @Expose()
    public site_name?: string;
    @Expose()
    public site_web_url?: string;
    @Expose()
    @Transform(forceMap(DatasourceOffice365MetadataDrive), { toClassOnly: true })
    public site_drives: Map<string, DatasourceOffice365MetadataDrive>;
}
export class DatasourceOffice365Metadata {
    @Expose()
    public tenant_id: string;
    @Expose()
    public secret_name?: string;
    @Expose()
    public scopes: string[] = [];
    @Expose()
    @Transform(anyToDate)
    public refresh_token_expire_at?: Date;
    @Expose()
    @Transform(forceMap(DatasourceOffice365MetadataSite), { toClassOnly: true })
    public sites: Map<string, DatasourceOffice365MetadataSite>;
    @Expose()
    @Transform(forceMap(DatasourceOffice365MetadataDrive), { toClassOnly: true })
    public drives: Map<string, DatasourceOffice365MetadataDrive>;
}

export class DatasourceFolderSyncState {
    @Expose()
    public delta_link: string | null = null;
    @Expose()
    @Transform(anyToDate)
    public last_sync_time: Date | null = null;
}

export class DatasourceOffice365Configuration {
    @Expose()
    public drives_enabled: string[] = [];
    @Expose()
    public folder_discovery_enabled: boolean = true;
    @Expose()
    public folder_filtering_enabled: boolean = true;
    @Expose()
    public folders_enabled: string[] = [];
    @Expose()
    public folders_excluded: string[] = [];
    @Expose()
    @Transform(forceMap(DatasourceFolderSyncState), { toClassOnly: true })
    public folders_sync_state: Map<string, DatasourceFolderSyncState> = new Map();
}

export class DatasourceGoogleConfiguration {
    @Expose()
    public drives_enabled: string[] = [];
    @Expose()
    public folder_discovery_enabled: boolean = false;
    @Expose()
    public folder_filtering_enabled: boolean = false;
    @Expose()
    public folders_enabled: string[] = [];
    @Expose()
    public folders_excluded: string[] = [];
}

export class DatasourceOffice365OAuth {
    @Expose()
    public delegated_code?: string; // from delegated OAuth
    @Expose()
    public delegated_code_verifier?: string; // from delegated OAuth
    @Expose()
    public delegated_redirect_uri?: string; // from delegated OAuth
    @Expose()
    public scopes: string[] = [];

}

// ── Atlassian (Jira + Confluence) ──────────────────────────────────────────
// Shapes mirror core_models_services DatasourceAtlassian* (Firestore contract).

export class DatasourceAtlassianOAuth {
    // Ephemeral: written by the callback, consumed+cleared by the backend.
    @Expose()
    public code?: string;
    @Expose()
    public redirect_uri?: string;
    @Expose()
    public scopes: string[] = [];
}

export class DatasourceAtlassianProjectRef {
    @Expose()
    public id: string;
    @Expose()
    public key: string; // issue prefix, e.g. "MYAC"
    @Expose()
    public name: string;
}

export class DatasourceAtlassianSpaceRef {
    @Expose()
    public id: string;
    @Expose()
    public key: string;
    @Expose()
    public name: string;
}

export class DatasourceAtlassianMetadata {
    @Expose()
    public cloud_id: string;
    @Expose()
    public site_url?: string; // e.g. https://acme.atlassian.net
    @Expose()
    public site_name?: string;
    @Expose()
    public account_email?: string;
    @Expose()
    public scopes: string[] = [];
    @Expose()
    public secret_name?: string;
    @Expose()
    @Transform(anyToDate)
    public refresh_token_expire_at?: Date;
    // Catalogue discovered by the metadata-sync job
    @Expose()
    @Type(() => DatasourceAtlassianProjectRef)
    public jira_projects: DatasourceAtlassianProjectRef[] = [];
    @Expose()
    @Type(() => DatasourceAtlassianSpaceRef)
    public confluence_spaces: DatasourceAtlassianSpaceRef[] = [];
    @Expose()
    @Transform(anyToDate)
    public last_metadata_sync_time?: Date;
}

export class DatasourceAtlassianConfiguration {
    @Expose()
    public sync_jira: boolean = true;
    @Expose()
    public sync_confluence: boolean = true;
    @Expose()
    public sync_attachments: boolean = true;
    @Expose()
    public sync_comments: boolean = true;
    // Empty list = sync everything visible to the token
    @Expose()
    public selected_jira_project_ids: string[] = [];
    @Expose()
    public selected_confluence_space_ids: string[] = [];
}


/**
 * MYAC-1515 — the company connection a datasource signs in through
 * (`tenants/{tenant_id}/datasource_connections/{connection_id}`). Set at
 * creation and never changed: the datasource then has no sign-in of its own.
 */
export class DatasourceConnectionRef {
    @Expose()
    public tenant_id: string;
    @Expose()
    public connection_id: string;
}

export class Datasource extends _FirebaseDocument {
    @Expose()
    public name: string;
    @Expose()
    public type: DatasourceType;
    @Expose()
    public status: DatasourceStatus = DatasourceStatus.PENDING;
    // MYAC-1145 — MAC-managed datasource: configuration, credentials and triggers
    // are frozen for clients (enforced by firestore.rules); ops-only flag.
    @Expose()
    public locked: boolean = false;
    @Expose()
    @Type(() => DatasourceConnectionRef)
    public connection?: DatasourceConnectionRef;


    // Google Drive
    @Expose()
    @Type(() => DatasourceGoogleDriveMetadata)
    public google_metadata?: DatasourceGoogleDriveMetadata;
    @Expose()
    @Type(() => DatasourceGoogleOAuth)
    public google_oauth?: DatasourceGoogleOAuth;
    @Expose()
    @Type(() => DatasourceGoogleConfiguration)
    public google_configuration?: DatasourceGoogleConfiguration;

    // Office 365
    @Expose()
    @Type(() => DatasourceOffice365Metadata)
    public office365_metadata?: DatasourceOffice365Metadata;
    @Expose()
    @Type(() => DatasourceOffice365OAuth)
    public office365_oauth?: DatasourceOffice365OAuth;
    @Expose()
    @Type(() => DatasourceOffice365Configuration)
    public office365_configuration?: DatasourceOffice365Configuration;

    // Atlassian (Jira + Confluence)
    @Expose()
    @Type(() => DatasourceAtlassianMetadata)
    public atlassian_metadata?: DatasourceAtlassianMetadata;
    @Expose()
    @Type(() => DatasourceAtlassianOAuth)
    public atlassian_oauth?: DatasourceAtlassianOAuth;
    @Expose()
    @Type(() => DatasourceAtlassianConfiguration)
    public atlassian_configuration?: DatasourceAtlassianConfiguration;

    @Expose()
    public is_syncing?: boolean;
    @Expose()
    @Transform(anyToDate)
    public last_sync_time?: Date;
    @Expose()
    public trigger_full_sync?: DatasourceTrigger;
    @Expose()
    public trigger_delta_sync?: DatasourceTrigger;
    @Expose()
    public trigger_delete?: DatasourceTrigger;
    @Expose()
    public trigger_google_oauth?: DatasourceTrigger;
    @Expose()
    public trigger_office365_oauth?: DatasourceTrigger;
    @Expose()
    public trigger_atlassian_oauth?: DatasourceTrigger;
    @Expose()
    public trigger_sites_metadata_sync?: DatasourceTrigger;
    @Expose()
    public trigger_tracex_auth?: DatasourceTrigger;
    @Expose()
    public trigger_tracex_list_projects?: DatasourceTrigger;
    @Expose()
    public trigger_qualishare_auth?: DatasourceTrigger;
    @Expose()
    public trigger_odoo_auth?: DatasourceTrigger;

    // TraceX
    @Expose()
    public tracex_credentials?: {
        url?: string;
        workspace?: string;
        email?: string;
        token?: string;
    };
    @Expose()
    public tracex_metadata?: {
        base_url?: string;
        workspace_id?: string;
        secret_name?: string;
        /** Email used for login (non-secret display); set after auth */
        account_email?: string;
        /** True if API token auth, false if email/password */
        auth_uses_token?: boolean;
        /** @deprecated prefer sync_total_documents after full sync — rarely set */
        document_count?: number;
        total_syncable_objects?: number;
        counts?: Record<string, number>;
        /**
         * Liste des projets TraceX disponibles dans le workspace, alimentée par
         * `tracex-sync` après `trigger_tracex_list_projects = "requested"`.
         * `ref` est opaque côté front (en réalité un `_id` Huly).
         */
        projects?: Array<{
            ref: string;
            identifier: string;
            name: string;
        }>;
        /** Timestamp ISO de la dernière mise à jour de `projects` (best-effort). */
        projects_listed_at?: string;
    };

    // Qualishare
    @Expose()
    public qualishare_credentials?: {
        url?: string;
        email?: string;
        password?: string;
    };
    @Expose()
    public qualishare_configuration?: {
        /** Liste des clés de portails activés pour la synchro (ex: ['Qualite', 'DM']). Vide = tous. */
        portals_enabled?: string[];
    };
    @Expose()
    public qualishare_metadata?: {
        base_url?: string;
        secret_name?: string;
        /** Email / identifiant (non-secret display); set after auth */
        account_email?: string;
        /** @deprecated prefer sync_total_documents after full sync — rarely set */
        document_count?: number;
        total_syncable_objects?: number;
        counts?: Record<string, number>;
    };

    // Odoo
    @Expose()
    public odoo_credentials?: {
        url?: string;
        database?: string;
        login?: string;
        api_key?: string;
        password?: string;
    };
    @Expose()
    public odoo_configuration?: {
        /** Modèles Odoo à synchroniser (ex: ['documents.document', 'ir.attachment']). */
        modules_enabled?: string[];
    };
    @Expose()
    public odoo_metadata?: {
        base_url?: string;
        database?: string;
        secret_name?: string;
        /** Login Odoo utilisé pour la connexion (non-secret display) */
        account_login?: string;
        /** Version d'Odoo détectée (ex: "saas~17.4+e", "17.0") */
        odoo_version?: string;
        /** Variante d'API utilisée pour la connexion (ex: "jsonrpc") */
        api_flavor?: string;
        /** @deprecated prefer sync_total_documents after full sync — rarely set */
        document_count?: number;
        total_syncable_objects?: number;
        counts?: Record<string, number>;
    };

    // Last sync error message (set by the backend when a sync job fails)
    @Expose()
    public last_sync_error: string | null = null;

    // Documents skipped during the last sync because their PDF rendition exceeded size limits
    @Expose()
    public sync_skipped_docs: Array<{ title: string; reason: 'text_too_large' | 'images_too_large' }> | null = null;

    // Sync progress details
    @Expose()
    public sync_total_documents: number | null = null;
    @Expose()
    public sync_documents_synced: number | null = null;
    /** Nombre de documents dans la datasource (mis à jour par le pipeline d'ingestion). */
    @Expose()
    public document_count: number | null = null;
    @Expose()
    public sync_current_phase: string | null = null;
    @Expose()
    public sync_current_file: string | null = null;
    // Phase-based progress (each phase = 1/sync_phase_count of total)
    @Expose()
    public sync_phase_index: number | null = null;
    @Expose()
    public sync_phase_count: number | null = null;
    @Expose()
    public sync_phase_synced: number | null = null;
    @Expose()
    public sync_phase_total: number | null = null;

    // Metadata sync progress
    @Expose()
    public metadata_sync_progress: number | null = null;
    @Expose()
    public metadata_sync_total_drives: number | null = null;
    @Expose()
    public metadata_sync_drives_processed: number | null = null;
    @Expose()
    public metadata_sync_folders_scanned: number | null = null;

    static fromGoogleMetadata(metadata: DatasourceGoogleDriveMetadata): Datasource {
        const d = new Datasource();
        d.google_metadata = metadata;
        d.status = DatasourceStatus.CONNECTED;
        d.is_syncing = false;
        return d;
    }

    collection(): string {
        return "datasources";
    }
    static fromFirebase(o: DocumentSnapshot): Datasource | null {
        return _FirebaseDocument._fromFirebase(Datasource, o);
    }
}

export class DatasourceFolder extends _FirebaseDocument {
    @Expose()
    public drive_id: string;
    @Expose()
    public folder_path: string;
    @Expose()
    public folder_name: string;
    @Expose()
    public parent_folder_id: string | null = null;
    @Expose()
    public has_children: boolean = false;
    @Expose()
    public is_root: boolean = false;

    @Expose()
    public children_discovered: boolean = false;
    @Expose()
    public trigger_discover_children: string = "idle";
    @Expose()
    public is_discovering_children: boolean = false;
    @Expose()
    @Transform(anyToDate)
    public children_discovered_at: Date | null = null;

    collection(): string {
        return "folders";
    }

    static collectionPath(projectId: string, datasourceId: string): string {
        return `projects/${projectId}/datasources/${datasourceId}/folders`;
    }

    static fromFirebase(o: DocumentSnapshot): DatasourceFolder | null {
        return _FirebaseDocument._fromFirebase(DatasourceFolder, o);
    }
}
